import { styles } from '.';
import { ButtonVariantStyles } from '../../../components/DynamicButton/DynamicButton.styles';
import { useResponsiveStyles } from '../../../utils/useResponsiveStyles';
import { ButtonVariant, HEADER_STATE, hexToRgba, pxToNumber, removeTransparency } from '../../Commons';

export const useHeaderVariantThreeStyles = ({
  globalStyles,
  localStyles,
  background,
  rightButton,
  headerState,
  scrolled,
  builderMode,
  isDesktop,
  isActive,
  isWholeNavbarParsed,
  designPreview,
}: any) => {
  const rightButtonVariant: ButtonVariant = rightButton?.variant?.value;
  const buttonVariantStyles = ButtonVariantStyles?.[rightButtonVariant || ButtonVariant.PRIMARY];
  const { createResponsiveStyles } = useResponsiveStyles(isDesktop, localStyles, globalStyles);

  const navLinkBaseStyles = {
    ...styles.NavLink,
    ...globalStyles.bodyText,
    ...localStyles.navbarLinkStyles,
    lineHeight: '30px',
    padding: '0 10px',
  };

  const normalizedFontSize = (localStyles?.navbarLinkStyles?.fontSize ?? globalStyles?.bodyText?.fontSize) || undefined;

  const navLinkStyles = {
    ...createResponsiveStyles(navLinkBaseStyles, 'navbarLinkStyles', 'bodyText'),
    ...(normalizedFontSize ? { fontSize: normalizedFontSize } : {}),
  };
  const navLinkColor = (navLinkStyles as any)?.color ?? navLinkBaseStyles.color;
  const navLinkFontSize = (navLinkStyles as any)?.fontSize ?? navLinkBaseStyles.fontSize;

  const navLinkStyles_Mobile = (isChild: boolean) => ({
    ...navLinkStyles,
    lineHeight: 1,
    justifyContent: 'space-between',
    height: isChild ? '30px' : '55px',
  });

  const navLinksContainerStyles = {
    ...styles.NavLinksContainer,
    visibility: isDesktop && isWholeNavbarParsed ? 'visible' : 'hidden',
    justifyContent: 'space-between',
    borderRadius: '50px',
    backgroundColor: background.value || globalStyles.backgroundColor,
    backdropFilter: `blur(${background.blur || 1}px)`,
    width: 'auto',
    gap: '0',
    margin: '0',
    '@media (max-width: 780px)': {
      ...styles.NavLinksContainer['@media (max-width: 780px)'],
      ...(globalStyles?.backgroundColor?.length > 0 && {
        backgroundColor: hexToRgba(globalStyles.backgroundColor, 0.5),
      }),
      ...(background?.value?.length > 0 && { backgroundColor: hexToRgba(background.value, 0.5) }),
    },
  };

  const navLinkContainerStyles = (isActive: boolean, isDesktop: boolean) => {
    return isActive
      ? isDesktop
        ? navLinksContainerStyles
        : { ...navLinksContainerMobileStyles(isActive), ...menuActiveMobileStyles }
      : isDesktop
      ? navLinksContainerStyles
      : navLinksContainerMobileStyles(isActive);
  };

  const navLinksContainerMobileStyles = (isActive: boolean) => ({
    ...styles.NavLinksContainer_Mobile,
    ...(globalStyles?.backgroundColor?.length > 0 && {
      backgroundColor: hexToRgba(globalStyles.backgroundColor, 0.5),
    }),
    ...(background?.value?.length > 0 && { backgroundColor: hexToRgba(background.value, 0.5) }),
  });

  const IconStyles = {
    ...styles.Icon,
    ...localStyles.navbarLinkStyles,
  };

  const buttonStyles = {
    textDecoration: 'none',
    ...buttonVariantStyles,
    ...globalStyles.buttonVariants?.[rightButton?.variant?.value || ButtonVariant.PRIMARY],
    ...localStyles.buttonVariants?.[rightButton?.variant?.value || ButtonVariant.PRIMARY],
    maxWidth: '100%',
    margin: '20px 0',
    borderRadius: globalStyles.button.borderRadius,
    fontFamily: globalStyles.bodyText.fontFamily,
    padding: isDesktop ? '12px 24px' : '5px 20px',
    fontSize: isDesktop ? '18px' : '12px',
    '@media (max-width: 780px)': {
      fontSize: '12px !important',
      padding: '5px 20px !important',
    },
  };

  const dropdownContainerStyles = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    height: '70px',
  };

  const dropdownButtonStyles = {
    ...navLinkStyles,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };

  const dropdownMenuStyles = {
    position: 'absolute',
    top: '70px',
    right: 0,
    backgroundColor: background.value || globalStyles.backgroundColor,
    backdropFilter: `blur(${background.blur || 1}px)`,
    boxShadow: '0 4px 8px rgba(0,0,0,0.5)',
    borderRadius: '4px',
    zIndex: 1000,
    minWidth: '200px',
  };

  const fontSize = pxToNumber(navLinkFontSize || '');

  const dropdownMobileItemStyles = () => ({
    ...navLinkStyles,
    display: 'block',
    color: navLinkColor,
    paddingLeft: `30px`,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    lineHeight: 1,
    width: '100%',
    fontSize: `${(fontSize || 0) - 3}px`,
    marginTop: '10px',
  });

  const dropdownItemStyles = (isChild?: boolean, hasChildren?: boolean, isSubChild?: boolean, isLast?: boolean) => ({
    borderBottom: (isChild && isLast) || (isSubChild && !isLast) || hasChildren ? '0' : `1px solid ${navLinkColor}`,
    display: 'block',
    color: navLinkColor,
    padding: `12px 16px 12px  ${isSubChild ? '40px' : '16px'}`,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    lineHeight: 1,
    width: '100%',
    ...(isSubChild && { fontSize: `${(fontSize || 0) - 3}px` }),
  });

  const menuActiveMobileStyles = {
    ...styles.MenuActive_Mobile,
    ...(globalStyles?.backgroundColor?.length > 0 && {
      backgroundColor: hexToRgba(globalStyles.backgroundColor, 0.8),
    }),
    ...(background?.value?.length > 0 && { backgroundColor: hexToRgba(background.value, 0.8) }),
  };

  const getStickyHeaderLeft = () => {
    if (isDesktop) return 'inherit';

    if (builderMode) {
      if (designPreview === undefined) return '48.9%';
      if (designPreview === true) return '50%';
      return '61.5%';
    }

    return '50%';
  };

  const getStickyHeaderWidth = () => {
    if (builderMode) {
      if (designPreview === undefined) return '95%';

      if (designPreview) {
        return isDesktop ? '94.8% !important' : '410px ';
      }

      return isDesktop ? '100% !important' : isActive ? '410px' : '390px';
    }

    if (isDesktop) return '100% !important';

    return !isActive ? '95% !important' : '100% !important';
  };

  const getDefaultHeaderWidth = () => {
    if (designPreview === undefined) {
      return isActive ? '100%' : !isDesktop ? '95%' : '100%';
    }
    return isDesktop ? '100%' : isActive ? '100%' : '390px';
  };

  const getDefaultHeaderLeft = () => {
    if (isDesktop) return 'inherit';

    if (builderMode) {
      return designPreview === undefined ? '49%' : '50%';
    }

    return '50%';
  };

  const getHeaderStyles = () => {
    let baseStyles: any = isDesktop
      ? { ...styles.Navbar, background: 'transparent', position: 'absolute' }
      : styles.Navbar_Mobile;

    switch (headerState) {
      case HEADER_STATE.STICKY:
        baseStyles = {
          ...baseStyles,
          position: designPreview === undefined ? 'relative' : 'fixed',
          top: builderMode ? 'inherit' : isActive ? '0px' : '10px',
          left: getStickyHeaderLeft(),
          width: getStickyHeaderWidth(),
        };
        break;
      default:
        baseStyles = {
          ...baseStyles,
          width: getDefaultHeaderWidth(),
          position: 'absolute',
          top: builderMode ? 'inherit' : isActive ? '0px' : isDesktop ? '0px' : '10px',
          left: getDefaultHeaderLeft(),
        };
    }

    return {
      ...baseStyles,
      ...(globalStyles?.backgroundColor?.length > 0 && {
        backgroundColor: isDesktop ? globalStyles.backgroundColor : removeTransparency(globalStyles.backgroundColor),
      }),
      ...(background?.value?.length > 0 && {
        backgroundColor: isDesktop ? background.value : removeTransparency(background.value),
      }),
      ...(!isDesktop && isActive && !builderMode ? { width: '100%', top: '0px' } : {}),
      marginTop: !builderMode ? 'inherit' : isDesktop ? '0px' : isActive ? '0' : '10px !important',
    };
  };

  const getHeaderContainerStyles = () => {
    return isDesktop
      ? { ...styles.HeaderContainer, justifyContent: 'space-between', padding: '20px 40px' }
      : { ...styles.MobileHeaderContainer, padding: isActive ? '0px 35px' : '0px 25px' };
  };

  const navlinkWrapperStyles = (
    isDropdown: boolean,
    isDesktop: boolean,
    primaryColor: string | null,
    isSelected?: boolean
  ) => {
    return {
      ...styles.NavlinkWrapper,
      width: isDesktop ? 'auto' : '100%',
      flexDirection: isDesktop ? 'row' : 'column',
      alignItems: isDesktop ? 'center' : 'normal',
      height: isDropdown || !isDesktop ? 'auto' : '70px',
      backgroundColor: isSelected && !isDropdown ? primaryColor : 'transparent',
      justifyContent: 'center',
      borderRadius: '50px',
    };
  };

  return {
    navLinkContainerStyles,
    navLinksContainerMobileStyles,
    IconStyles,
    buttonStyles,
    navLinkStyles,
    navLinkStyles_Mobile,
    dropdownContainerStyles,
    dropdownButtonStyles,
    dropdownMenuStyles,
    dropdownItemStyles,
    dropdownMobileItemStyles,
    menuActiveMobileStyles,
    getHeaderStyles,
    navlinkWrapperStyles,
    getHeaderContainerStyles,
  };
};
